import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Text } from 'grommet';
import { SubmissionLoader } from '@Components/Control';
import { OnboardingWizard, PrefillOnboardingWizard } from '@Components/Partial/Onboarding';
import { useAppTheme } from '@Components/Context';
import {
  fetchOnboardingPricingRequest,
  clearOnboardingPricingRequest,
  initiateSubmitOnboardingRequest,
  fetchPrefillOnboardingDataRequest,
  clearPrefillOnboardingDataRequest,
} from '@Actions';
import { paths } from '@Components/configs';

const CustomerOnboardingPage = ({
  small,
  size,
  loading,
  submitLoading,
  fetchPricing,
  clearPricing,
  initiateSubmit,
  fetchPrefillData,
  clearPrefillData,
  pricingData = null,
  submissionResponse = null,
  prefillData = null,
  prefillLoading = false,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme: { page, button } } = useAppTheme();

  const prefillToken = searchParams.get('token');
  const isPrefillMode = !!prefillToken;

  const urlReferralToken = searchParams.get('referral_token');
  const effectiveReferralToken = urlReferralToken || window.localStorage.getItem('referralToken');

  React.useEffect(() => {
    if (urlReferralToken) {
      window.localStorage.setItem('referralToken', urlReferralToken);
    }
  }, []);

  React.useEffect(() => {
    if (isPrefillMode) {
      fetchPrefillData(prefillToken);
      return () => clearPrefillData();
    }
    fetchPricing();
    return () => clearPricing();
  }, [isPrefillMode]);

  // Redirect to account setup on successful submission (both flows).
  React.useEffect(() => {
    if (submissionResponse?.onboardingId) {
      window.localStorage.setItem('onboardingId', submissionResponse.onboardingId);
      navigate(paths.accountSetup);
    }
  }, [submissionResponse]);

  return (
    <Box>
      {submitLoading && (
        <SubmissionLoader
          textColor={page.modalText}
          loaderColor={button.highlight}
          contentWidth="20rem"
        >
          <Box gap="0.65rem">
            <Text size="1.25rem" weight={600} color={page.primaryText}>
              Submitting your information...
            </Text>
            <Text size="0.85rem" color={page.secondaryText}>
              You will be redirected to account setup in a moment.
              Please do not refresh or close the page.
            </Text>
          </Box>
        </SubmissionLoader>
      )}
      {isPrefillMode ? (
        <PrefillOnboardingWizard
          small={small}
          size={size}
          loading={loading}
          submitLoading={submitLoading}
          initiateSubmit={initiateSubmit}
          prefillToken={prefillToken}
          prefillData={prefillData}
          prefillLoading={prefillLoading}
        />
      ) : (
        <OnboardingWizard
          small={small}
          size={size}
          loading={loading}
          submitLoading={submitLoading}
          initiateSubmit={initiateSubmit}
          referralToken={effectiveReferralToken}
          referralFromUrl={!!urlReferralToken}
          pricingData={pricingData}
          submissionResponse={submissionResponse}
        />
      )}
    </Box>
  );
};

function mapStateToProps(state) {
  return {
    loading: state.fetchLoader.dataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
    pricingData: state.payment.onboardingPricing,
    submissionResponse: state.payment.onboardingSubmissionResponse,
    prefillData: state.payment.prefillOnboardingData,
    prefillLoading: state.payment.prefillOnboardingDataLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchPricing: fetchOnboardingPricingRequest,
    clearPricing: clearOnboardingPricingRequest,
    initiateSubmit: initiateSubmitOnboardingRequest,
    fetchPrefillData: fetchPrefillOnboardingDataRequest,
    clearPrefillData: clearPrefillOnboardingDataRequest,
  }, dispatch);
}

CustomerOnboardingPage.propTypes = {
  small: PropTypes.bool.isRequired,
  size: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  fetchPricing: PropTypes.func.isRequired,
  clearPricing: PropTypes.func.isRequired,
  initiateSubmit: PropTypes.func.isRequired,
  fetchPrefillData: PropTypes.func.isRequired,
  clearPrefillData: PropTypes.func.isRequired,
  pricingData: PropTypes.shape({
    version: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    effectiveAt: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
    rules: PropTypes.shape({
      youtubeCid: PropTypes.shape({
        self: PropTypes.shape({
          revenueShare: PropTypes.shape({
            setupFeeAmount: PropTypes.number.isRequired,
            monthlyFeeAmount: PropTypes.number.isRequired,
            revenueSharePercent: PropTypes.number.isRequired,
          }).isRequired,
          hybrid: PropTypes.shape({
            setupFeeAmount: PropTypes.number.isRequired,
            monthlyFeeAmount: PropTypes.number.isRequired,
            initialSharePercent: PropTypes.number.isRequired,
            initialMonths: PropTypes.number.isRequired,
            afterSharePercent: PropTypes.number.isRequired,
          }).isRequired,
        }).isRequired,
        redflag: PropTypes.shape({
          revenueShare: PropTypes.shape({
            setupFeeAmount: PropTypes.number.isRequired,
            monthlyFeeAmount: PropTypes.number.isRequired,
            revenueSharePercent: PropTypes.number.isRequired,
          }).isRequired,
          hybrid: PropTypes.shape({
            setupFeeAmount: PropTypes.number.isRequired,
            monthlyFeeAmount: PropTypes.number.isRequired,
            initialSharePercent: PropTypes.number.isRequired,
            initialMonths: PropTypes.number.isRequired,
            afterSharePercent: PropTypes.number.isRequired,
          }).isRequired,
        }).isRequired,
      }).isRequired,
      socialScanning: PropTypes.shape({
        perPlatformMonthlyFeeAmount: PropTypes.number.isRequired,
      }).isRequired,
      watermarking: PropTypes.shape({
        setupFeeAmount: PropTypes.number.isRequired,
        monthlyFeeAmount: PropTypes.number.isRequired,
      }).isRequired,
    }).isRequired,
    display: PropTypes.shape({
      planLabels: PropTypes.shape({
        revenueShare: PropTypes.string.isRequired,
        hybrid: PropTypes.string.isRequired,
      }).isRequired,
      uploaderLabels: PropTypes.shape({
        self: PropTypes.string.isRequired,
        redflag: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }),
  submissionResponse: PropTypes.shape({
    message: PropTypes.string.isRequired,
    onboardingId: PropTypes.string,
    payment: PropTypes.shape({
      status: PropTypes.string.isRequired,
      invoice: PropTypes.shape({
        amount: PropTypes.number,
        invoiceId: PropTypes.string,
        invoiceUrl: PropTypes.string,
        invoiceStatus: PropTypes.string,
      }),
      recurring: PropTypes.shape({
        amount: PropTypes.number,
        scheduleId: PropTypes.string,
      }),
      totals: PropTypes.shape({
        setupFeeAmount: PropTypes.number,
        monthlyFeeAmount: PropTypes.number,
        initialInvoiceAmount: PropTypes.number,
      }),
    }).isRequired,
  }),
  prefillData: PropTypes.shape({
    submissionId: PropTypes.string,
    currency: PropTypes.string,
    contact: PropTypes.shape({
      email: PropTypes.string,
      fullName: PropTypes.string,
      companyName: PropTypes.string,
      phone: PropTypes.string,
      country: PropTypes.string,
    }),
    products: PropTypes.arrayOf(PropTypes.shape({
      productKey: PropTypes.string,
      productLabel: PropTypes.string,
      productType: PropTypes.string,
      plan: PropTypes.shape({
        name: PropTypes.string,
        data: PropTypes.object,
      }),
      config: PropTypes.object,
    })),
    extraItems: PropTypes.arrayOf(PropTypes.shape({
      kind: PropTypes.oneOf(['discount', 'charge']),
      valueType: PropTypes.oneOf(['amount', 'percent']),
      value: PropTypes.number,
      description: PropTypes.string,
      frequency: PropTypes.oneOf(['monthly', 'one_time']),
      productKey: PropTypes.string,
    })),
    terms: PropTypes.shape({
      source: PropTypes.oneOf(['default', 'custom']),
      body: PropTypes.string,
    }),
  }),
  prefillLoading: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomerOnboardingPage);
